// What a click means around a train (§31 S7). With your engine selected, a
// click on your track orders it there; with your car selected, a click on a
// square orthogonally beside it sets its first rider down; with your piece
// selected, a click on your car beside it boards it. Each refusal is said.

import { TRAIN_CAR, TRAIN_ENGINE, structureAt } from "src/engine/rules/industry/state";
import {
  alightError,
  boardError,
  driveError,
  railed,
  type TrainError
} from "src/engine/rules/industry/trains";
import { pieceAt } from "src/engine/rules/position";
import { pieceColor } from "src/shared/board";
import type { SessionCore } from "./core";
import { REFUSAL, TRAIN_SAYS } from "./refusals";

/** Send `command` or say why not. True when the click was a train's. */
function act(core: SessionCore, why: TrainError | null, command: Parameters<SessionCore["send"]>[0]): true {
  if (!core.controlsTurn) core.setRefusal(REFUSAL["not-your-turn"]);
  else if (why) core.setRefusal(TRAIN_SAYS[why]);
  else core.send(command);
  return true;
}

/** Handle `square` as a train click, or answer false to fall through. */
export function trainClick(core: SessionCore, square: number): boolean {
  const selected = core.selected;
  if (selected === undefined || selected === square) return false;
  const { state, localSide } = core;
  const { position, industry } = state;
  const from = structureAt(industry, selected);
  const there = structureAt(industry, square);

  if (from?.kind === TRAIN_ENGINE && from.owner === localSide) {
    if (pieceAt(position, square) !== 0 || !railed(industry, square, localSide)) return false;
    return act(core, driveError(industry, localSide, selected, square), {
      type: "drive-train",
      at: selected,
      to: square
    });
  }
  if (from?.kind === TRAIN_CAR && from.owner === localSide && (from.train?.riders?.length ?? 0) > 0) {
    if (pieceAt(position, square) !== 0) return false;
    const why = alightError(position, industry, localSide, selected, square);
    // Not beside the car: an ordinary click elsewhere.
    if (why === "not-orthogonal") return false;
    return act(core, why, { type: "alight-train", car: selected, to: square });
  }
  const piece = pieceAt(position, selected);
  if (piece !== 0 && pieceColor(piece) === localSide && there?.kind === TRAIN_CAR && there.owner === localSide) {
    return act(core, boardError(position, industry, localSide, square, selected), {
      type: "board-train",
      car: square,
      from: selected
    });
  }
  return false;
}
